// Shared design tokens for the Calls > Overview and Calls > Reports pages —
// one source of truth so both pages render identical colors/fonts instead of
// two hand-maintained copies drifting apart.
//
// A curated, saturated set (the same family of hues most modern SaaS/VOIP
// dashboards use — Stripe, Linear, Vercel-style jewel tones) rather than the
// flatter default swatches: green = answered, red = missed, orange =
// abandoned, blue = live/connected/total, violet = declined, slate = failed
// (a system outcome, not a human one — deliberately desaturated so it
// doesn't compete with the others), yellow = still ringing.
//
// RING is the raw token — used as a thin graphical stroke or chart segment,
// not text, so no contrast requirement applies.
// FILL is a darkened on-color variant of the same token, computed (not
// eyeballed) so white tile text clears WCAG AA — several of the raw tones
// are under 3:1 against white, too low for a label on a solid fill. Each
// FILL value clears >=4.7:1 white-text contrast.
export const PALETTE = {
  good: { ring: "#16a34a", fill: "#12823b" },
  critical: { ring: "#e11d48", fill: "#e11d48" },
  serious: { ring: "#ea580c", fill: "#c74b0a" },
  blue: { ring: "#2563eb", fill: "#2563eb" },
  violet: { ring: "#7c3aed", fill: "#7c3aed" },
  yellow: { ring: "#eab308", fill: "#a16207" },
  warning: { ring: "#eab308" },
  muted: { ring: "#64748b", fill: "#64748b" },
};

// Fixed status -> color mapping, used everywhere a Call status is rendered
// (pie/donut segments, table badges, chart bars) so the same status always
// reads as the same color across both pages.
export const STATUS_COLORS = {
  completed: PALETTE.good.ring,
  connected: PALETTE.blue.ring,
  missed: PALETTE.critical.ring,
  abandoned: PALETTE.serious.ring,
  declined: PALETTE.violet.ring,
  failed: PALETTE.muted.ring,
  ringing: PALETTE.yellow.ring,
  connecting: PALETTE.yellow.ring,
};

export const STATUS_LABELS = {
  completed: "Answered",
  connected: "In progress",
  missed: "No answer",
  abandoned: "Abandoned",
  declined: "Declined",
  failed: "Failed",
  ringing: "Ringing",
  connecting: "Connecting",
};

export function formatDuration(seconds) {
  const s = Math.max(0, Math.round(seconds || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

// Cycle-long totals (call usage, total talk time) run into hours, unlike a
// single call's duration — formatDuration above stays minutes-only on
// purpose (a per-call duration is never that long).
export function formatHoursMinutes(seconds) {
  const s = Math.max(0, Math.round(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${s % 60}s`;
}

export function formatCount(n) {
  return (n ?? 0).toLocaleString("en-US");
}
